import DOMPurify from 'dompurify'

import { log } from 'src/services/app/log.service'

/**
 * Security validation helper functions
 * Centralized location for security-critical validation logic
 */

/**
 * Validate redirect route to prevent open redirect vulnerabilities
 * @param {string|null} route - Route to validate
 * @returns {boolean} - True if valid, false if invalid
 * @description Only accepts relative paths starting with '/' to prevent external redirects
 */
export function isValidRedirectRoute(route) {
  // Null is valid (clears redirect)
  if (route === null || route === undefined) {
    return true
  }

  // Must be a string
  if (typeof route !== 'string') {
    log.error('isValidRedirectRoute: Route must be a string', {
      route,
      type: typeof route,
    })
    return false
  }

  // Must start with '/' (relative path only)
  if (!route.startsWith('/')) {
    log.error('isValidRedirectRoute: Route must start with / (relative path)', {
      route,
    })
    return false
  }

  // Prevent protocol-relative URLs (//evil.com)
  if (route.startsWith('//')) {
    log.error('isValidRedirectRoute: Protocol-relative URLs not allowed', {
      route,
    })
    return false
  }

  // Prevent backslash bypasses (\\ or \/)
  if (route.includes('\\')) {
    log.error('isValidRedirectRoute: Backslashes not allowed in routes', {
      route,
    })
    return false
  }

  // Prevent absolute URL patterns (http://, https://, etc.)
  if (route.includes('://')) {
    log.error('isValidRedirectRoute: Absolute URL patterns not allowed', {
      route,
    })
    return false
  }

  return true
}

/**
 * Sanitize HTML to allow only safe formatting tags
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized HTML safe for rendering
 * @description Strict allowlist: only permits <mark>, <b>, <strong>, <em>, <i> tags with no attributes
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') {
    return ''
  }

  // Strict configuration: only allow safe formatting tags
  const config = {
    ALLOWED_TAGS: ['mark', 'b', 'strong', 'em', 'i'],
    ALLOWED_ATTR: [], // No attributes allowed (no class, no style, nothing)
    KEEP_CONTENT: true, // Keep text content even if tags are removed
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
  }

  try {
    const sanitized = DOMPurify.sanitize(html, config)
    return sanitized
  } catch (error) {
    log.error('sanitizeHtml: Sanitization failed', { html, error })
    // Return plain text on error (strip all HTML)
    return html.replace(/<[^>]*>/g, '')
  }
}

/**
 * Sanitize request payload for error reporting (PostHog/Slack).
 * Redacts sensitive fields, skips FormData (files can't be serialized).
 * @param {Object|FormData|null} data - Request payload
 * @returns {Object|undefined} - Sanitized payload or undefined if nothing to report
 */
const SENSITIVE_KEYS = [
  'account_id',
  'token',
  'text',
  'problem_description',
  'email',
  'code',
  'user_info',
]

export function sanitizePayload(data, deep = false) {
  if (data === null) return null
  if (data === undefined) return undefined
  if (data instanceof FormData) return 'Form Data'

  let parsed = data
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data)
    } catch {
      return undefined
    }
  }

  if (typeof parsed !== 'object') return parsed
  if (Array.isArray(parsed)) {
    return deep ? parsed.map((item) => sanitizePayload(item, true)) : parsed
  }

  const sanitized = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (SENSITIVE_KEYS.includes(key)) {
      sanitized[key] = _redact(value)
    } else if (deep && value && typeof value === 'object') {
      sanitized[key] = sanitizePayload(value, true)
    } else {
      sanitized[key] = value
    }
  }
  return sanitized
}

function _redact(value) {
  if (typeof value !== 'string' || value.length <= 6) return '[REDACTED]'
  return `${value.slice(0, 3)}...${value.slice(-3)}`
}

/**
 * Validate URL protocol to prevent dangerous protocols
 * @param {string} url - URL to validate
 * @returns {Object} - { isValid: boolean, normalizedUrl: string|null, error: string|null }
 */
export function validateUrlProtocol(url) {
  if (!url || typeof url !== 'string') {
    return {
      isValid: false,
      normalizedUrl: null,
      error: 'URL must be a non-empty string',
    }
  }

  // Normalize URL
  let normalizedUrl = url.trim()

  // Add https:// if no protocol specified
  if (!normalizedUrl.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/)) {
    normalizedUrl = `https://${normalizedUrl}`
  }

  // Validate protocol using URL parsing
  try {
    const urlObj = new URL(normalizedUrl)
    const allowedProtocols = ['http:', 'https:']

    if (!allowedProtocols.includes(urlObj.protocol)) {
      log.error('validateUrlProtocol: Dangerous protocol blocked', {
        url,
        protocol: urlObj.protocol,
      })
      return {
        isValid: false,
        normalizedUrl: null,
        error: `Protocol ${urlObj.protocol} not allowed. Only http: and https: are permitted.`,
      }
    }

    return {
      isValid: true,
      normalizedUrl,
      error: null,
    }
  } catch (error) {
    log.error('validateUrlProtocol: Invalid URL format', { url, error })
    return {
      isValid: false,
      normalizedUrl: null,
      error: 'Invalid URL format',
    }
  }
}
